import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';

import {
  useGetMyAgencyShiftsQuery,
  useReassignAgencyShiftMutation,
  useCancelAgencyShiftMutation,
} from '../slices/agencyShiftApiSlice';
import WorkerAssignDialog from '../components/WorkerAssignDialog';

// A worker can be changed on any shift here at any time before it starts —
// the backend enforces that cutoff (and ownership, and cancelled/finalised
// state) independently, this screen just reflects it back with a disabled
// button once a shift has started.
const MyShiftsScreen = () => {
  const { data: shifts, isLoading, isFetching, error, refetch } = useGetMyAgencyShiftsQuery();
  const [reassign, { isLoading: isReassigning }] = useReassignAgencyShiftMutation();
  const [cancelShift, { isLoading: isCancelling }] = useCancelAgencyShiftMutation();
  const [dialogShift, setDialogShift] = useState(null);
  const [cancelDialogShift, setCancelDialogShift] = useState(null);

  const handleReassignSubmit = async ({ workerName, workerContact, workerEmail }) => {
    try {
      await reassign({
        shiftId: dialogShift._id,
        workerName,
        workerContact,
        workerEmail,
      }).unwrap();
      toast.success('Assigned worker updated');
      setDialogShift(null);
    } catch (err) {
      toast.error(err?.data?.message || 'Could not update the assigned worker');
    }
  };

  // Unlike Change Worker, cancelling is allowed even after the shift has
  // started (the same as the admin's own "Cancel Agency Shift") — only
  // blocked once it's already cancelled or its outcome has been recorded,
  // both of which the backend re-checks independently.
  const canCancel = (shift) =>
    !shift.agencyCancelled &&
    shift.actualStatus !== 'Complete' &&
    shift.actualStatus !== 'Not-Worked' &&
    !shift.supersededByShiftId;

  const handleCancelShift = async () => {
    if (!cancelDialogShift) return;
    try {
      await cancelShift({ shiftId: cancelDialogShift._id }).unwrap();
      toast.success('Shift cancelled — the nursing bank has been notified');
      setCancelDialogShift(null);
    } catch (err) {
      toast.error(err?.data?.message || 'Could not cancel this shift');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 700 }}>
          My Shifts
        </Typography>
        <IconButton onClick={() => refetch()} disabled={isFetching}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.data?.message || 'Failed to load your shifts'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !shifts?.length ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Your agency hasn't grabbed any shifts yet.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Ward</TableCell>
                <TableCell>Ref</TableCell>
                <TableCell>Worker</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shifts.map((shift) => {
                const start = new Date(shift.startDateTime);
                const end = new Date(shift.endDateTime);
                const hasStarted = start <= new Date();
                const isCancelled = shift.agencyCancelled;

                return (
                  <TableRow key={shift._id} hover>
                    <TableCell>{start.toLocaleDateString()}</TableCell>
                    <TableCell>
                      {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {' – '}
                      {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </TableCell>
                    <TableCell>{shift.wardDepartmentArea}</TableCell>
                    <TableCell>{shift.poNumber || '—'}</TableCell>
                    <TableCell>{shift.filledByAgencyWorkerName || '—'}</TableCell>
                    <TableCell>
                      {isCancelled ? (
                        <Chip size="small" color="error" label="Cancelled" />
                      ) : shift.actualStatus === 'Complete' ? (
                        <Chip size="small" color="success" label="Approved" />
                      ) : shift.actualStatus === 'Not-Worked' ? (
                        <Chip size="small" color="warning" label="Not Approved" />
                      ) : hasStarted ? (
                        <Chip size="small" label="In progress / past" />
                      ) : (
                        <Chip size="small" color="success" label="Upcoming" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={hasStarted || isCancelled}
                          onClick={() => setDialogShift(shift)}
                        >
                          Change Worker
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={!canCancel(shift)}
                          onClick={() => setCancelDialogShift(shift)}
                        >
                          Cancel Shift
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <WorkerAssignDialog
        open={!!dialogShift}
        onClose={() => setDialogShift(null)}
        onSubmit={handleReassignSubmit}
        isSubmitting={isReassigning}
        shift={dialogShift}
        title="Change assigned worker"
        submitLabel="Save"
        initialWorker={
          dialogShift
            ? {
                name: dialogShift.filledByAgencyWorkerName,
                contact: dialogShift.filledByAgencyWorkerContact,
                email: dialogShift.filledByAgencyWorkerEmail,
              }
            : null
        }
      />

      <Dialog open={!!cancelDialogShift} onClose={() => setCancelDialogShift(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel this shift?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This can't be undone from here. The shift stays on record as cancelled by your agency —
            the Ref{cancelDialogShift?.poNumber ? ` (${cancelDialogShift.poNumber})` : ''} and worker
            details are kept for invoicing, and the nursing bank will be emailed automatically. If you
            still need to arrange cover, contact Beaumont directly.
          </Alert>
          {cancelDialogShift && (
            <Box>
              <Typography variant="body2">
                <strong>Ward:</strong> {cancelDialogShift.wardDepartmentArea}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {new Date(cancelDialogShift.startDateTime).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Time:</strong>{' '}
                {new Date(cancelDialogShift.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {' – '}
                {new Date(cancelDialogShift.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
              <Typography variant="body2">
                <strong>Worker:</strong> {cancelDialogShift.filledByAgencyWorkerName || '—'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogShift(null)}>Back</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelShift}
            disabled={isCancelling}
          >
            {isCancelling ? <CircularProgress size={22} /> : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyShiftsScreen;
